const API_BASE = '/api/compliance/monitoring';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface MonitoringDashboard {
  evidence_health: { total: number; uploaded: number; verified: number; expired: number };
  capa_overview: { total: number; open: number; overdue: number };
  task_completion: { total: number; completed: number; overdue: number };
  framework_status: { assigned: number; active: number };
  certification_expiry: { expiring_30: number; expiring_90: number };
  high_risk_count: number;
  policies_due_review: number;
  overall_score: number;
}

function mapSnapshot(raw: any): MonitoringDashboard {
  // Map the server's MonitoringSnapshot shape to what the dashboard component expects.
  const evidenceH = raw.evidence_health || {};
  const capaStats = raw.capa_stats || {};
  const byStatus: Record<string, number> = capaStats.by_status || {};
  const taskStats = raw.task_stats || {};
  const certArray: any[] = Array.isArray(raw.certification_expiry) ? raw.certification_expiry : [];
  const fwArray: any[] = Array.isArray(raw.framework_compliance) ? raw.framework_compliance : [];
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
  const in90 = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();

  const evidenceTotal = (evidenceH.expired || 0) + (evidenceH.expiring_30d || 0) + (evidenceH.expiring_60d || 0) + (evidenceH.healthy || 0);
  const capaOpen = capaStats.total_open ?? Object.values(byStatus).reduce((a: number, b: any) => a + Number(b), 0);
  const capaOverdue = byStatus['overdue'] || 0;

  return {
    evidence_health: {
      total: evidenceTotal,
      uploaded: evidenceTotal,
      verified: evidenceH.healthy || 0,
      expired: evidenceH.expired || 0,
    },
    capa_overview: {
      total: capaOpen,
      open: capaOpen,
      overdue: capaOverdue,
    },
    task_completion: {
      total: Number(taskStats.total ?? 0),
      completed: Number(taskStats.completed ?? 0),
      overdue: Number(raw.overdue_tasks ?? 0),
    },
    framework_status: {
      assigned: fwArray.length,
      active: fwArray.filter((f: any) => f.score > 0).length,
    },
    certification_expiry: {
      expiring_30: certArray.filter((c: any) => c.expiry_date && c.expiry_date <= in30).length,
      expiring_90: certArray.filter((c: any) => c.expiry_date && c.expiry_date <= in90).length,
    },
    high_risk_count: Number(raw.high_risk_count ?? 0),
    policies_due_review: Number(raw.policies_due_for_review ?? raw.policies_due_review ?? 0),
    overall_score: Number(raw.overall_score ?? 0),
  };
}

export const monitoringApi = {
  async getDashboard(companyId?: number): Promise<MonitoringDashboard> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/dashboard${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return mapSnapshot(result.data || result);
  },

  async getHealth(companyId?: number): Promise<Partial<MonitoringDashboard>> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/health${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
